import Credentials from './Credentials';

const globals = {
    // Constants (the environment's values are src/Credentials.js; npm start = the local func host, npm run build = the deployed backend)
    serverUrl: process.env.NODE_ENV === 'production' ? Credentials.serverUrl : Credentials.localServerUrl,
    languageNames: {
        "EN": "English", "PT": "Portuguese", "ID": "Indonesian", "KO": "Korean", "TR": "Turkish", "AZ": "Azerbaijani", "PL": "Polish",
        "FR": "French", "FA": "Persian", "HI": "Hindi", "AR": "Arabian", "VI": "Vietnamese", "ZH": "Chinese", "IT": "Italian",
        "TH": "Thai", "DE": "German", "ES": "Spanish", "RU": "Russian", "JA": "Japanese", "AF": "Afrikaans", "SQ": "Albanian",
        "BS": "Bosnian", "CA": "Catalan", "DA": "Danish", "SK": "Slovak", "SL": "Slovenian", "ET": "Estonian", "FI": "Finnish",
        "RO": "Romanian", "SV": "Swedish", "CS": "Czech", "UZ": "Uzbek", "ZU": "Zulu", "NL": "Dutch", "HU": "Hungarian", "LT": "Lithuanian",
        "MS": "Malay", "BE": "Belarusian", "BG": "Bulgarian", "KK": "Kazakh", "MK": "Macedonian", "MN": "Mongolian", "SR": "Serbian",
        "UK": "Ukrainian", "IW": "Hebrew", "MR": "Marathi"
    },
    platformNames: ["Android", "iOS", "Web", "Steam"],
    // ISO 3166-1 alpha-2, the backend's CountryMapping.CountryCode list (names come from Intl.DisplayNames)
    countryCodes: ["US", "CA", "BR", "MX", "AR", "CO", "CL", "PE", "VE", "EC", "GT", "CU", "BO", "DO", "HN", "PY", "SV", "NI", "CR", "PA", "UY", "HT", "JM", "TT", "GY",
        "SR", "BZ", "BS", "KN", "DM", "AG", "GD", "VC", "LC", "BB", "PR", "VI", "AS", "MP", "GU", "UM", "GF", "GP", "MQ", "BL", "MF", "PM", "AW", "CW", "SX",
        "BQ", "VG", "KY", "TC", "MS", "AI", "FK", "GS", "GB", "DE", "FR", "IT", "ES", "PT", "PL", "UA", "RO", "NL", "BE", "SE", "NO", "DK", "FI", "CH", "AT",
        "HU", "CZ", "SK", "GR", "BG", "HR", "RS", "BY", "IE", "GE", "BA", "AM", "LT", "AL", "SI", "MK", "LV", "ME", "EE", "LU", "MT", "IS", "CY", "MD", "XK",
        "AD", "SM", "MC", "LI", "VA", "FO", "GL", "GI", "BM", "GG", "JE", "IM", "SJ", "AX", "RE", "YT", "TF", "SH", "IO", "JP", "KR", "CN", "TW", "HK", "SG",
        "IN", "ID", "PH", "VN", "TH", "MY", "PK", "BD", "SA", "AE", "IL", "IQ", "IR", "TR", "RU", "KZ", "UZ", "AZ", "AF", "MM", "SY", "KP", "YE", "NP", "LK",
        "KH", "TJ", "PG", "LA", "KG", "TM", "JO", "PS", "OM", "KW", "QA", "MN", "BN", "TL", "BT", "MV", "CM", "BH", "LB", "MH", "FM", "PW", "NR", "TV", "TO",
        "MO", "CK", "NU", "NF", "CX", "CC", "ZA", "EG", "NG", "KE", "MA", "GH", "ET", "TZ", "CD", "SD", "DZ", "UG", "AO", "MZ", "MG", "CI", "NE", "BF", "ML",
        "MW", "ZM", "SO", "TD", "SN", "ZW", "GN", "SS", "RW", "BJ", "BI", "TN", "TG", "LY", "SL", "CF", "CG", "LR", "MR", "GA", "GM", "LS", "GW", "GQ", "ER",
        "NA", "BW", "DJ", "KM", "SZ", "MU", "CV", "ST", "SC", "EH", "AU", "NZ", "FJ", "SB", "VU", "WS", "KI", "PF", "NC", "TK", "WF", "PN", "AQ", "BV", "HM",
],
    branches: ["Production", "Beta", "Internal", "Development"],
    branchFlags: {
        None:        0,
        Production:  1 << 0,
        Beta:        1 << 1,
        Internal:    1 << 2,
        Development: 1 << 3
    },

    // Vars
    loginData: {},
    privateKey: null,
    privateKeyDecrypt: null,
    backendPublicKey: null,
    backendPublicKeyEncrypt: null,
    selectedTitle: "",
    route: null,        // { title, page, sub, deep[] } of the page on screen (Layout/Router.js); pages read their deep link here
    routeNonce: 0,      // bumped when a deep link is opened, so the page mounts fresh

    // Methods
    branchToFlags(branch) {
        switch(branch) {
            case "Production":
                return this.branchFlags.Production;
            case "Beta":
                return this.branchFlags.Beta;
            case "Internal":
                return this.branchFlags.Internal;
            case "Development":
                return this.branchFlags.Development;
            default:
                return this.branchFlags.None;
        }
    },

    addBranchFlags(flags, branch) {
        return flags | this.branchToFlags(branch);
    },

    removeBranchFlags(flags, branch) {
        return flags & ~this.branchToFlags(branch);
    },

    checkBranchFlags(flags, branch) {
        return (flags & this.branchToFlags(branch)) !== this.branchFlags.None;
    },

    setBranchFlags(branchArray) {
        let flags = this.branchFlags.None;
        for (const branch of branchArray) {
            flags |= this.branchToFlags(branch);
        }
        return flags;
    },

    compactVersion(major, minor, patch, branch = null) {
        if (branch == null) branch = 0;
        return ((branch & 0x3) << 29) | ((major & 0xFF) << 21) | ((minor & 0xFF) << 13) | (patch & 0x1FFF);
    },

    versionChangePatch(version, patch) {
        const { branch, major, minor } = this.decompactVersion(version);
        return this.compactVersion(major, minor, patch, branch);
    },

    decompactVersion(version) {
        const patch = version & 0x1FFF;
        const minor = (version >> 13) & 0xFF;
        const major = (version >> 21) & 0xFF;
        const branch = (version >> 29) & 0x3;
        return { branch, major, minor, patch };
    }
};

export default globals;